import type { ApiClient } from '@carbonwebmail/api-client'
import type { SaveSegmentViewRequest, SegmentViewDto } from '@carbonwebmail/contracts'
import { useCallback, useEffect, useState } from 'react'

/**
 * The saved views above the segments board.
 *
 * **A view is not a segment**, which matters more here than anywhere: these
 * tabs sit directly above a list of audiences, and saving one must never make
 * another. Nothing in here writes a segment.
 *
 * **One instance, owned by the screen that draws the tabs.** The contacts board
 * learnt this the hard way: the strip and the dialog each called their own copy
 * of the equivalent hook, so saving reloaded one list while the tabs were drawn
 * from the other, and a view was created that nothing showed. The segments
 * board is one component, so there is one of these — and it stays that way.
 *
 * Loaded separately from the rows, and a failure leaves the board working with
 * no tabs above it. That is the board as it was before views existed — a worse
 * board, not a broken one — and taking the list down because the view list did
 * not answer would be the wrong trade.
 */
export function useSegmentViews({ client }: { readonly client: ApiClient }) {
  const [views, setViews] = useState<readonly SegmentViewDto[]>([])

  const load = useCallback(async () => {
    try {
      setViews((await client.segmentViews()).views)
    } catch {
      setViews([])
    }
  }, [client])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Saves a view, or replaces one whole when `viewId` is given.
   *
   * Replacing rather than patching is what renaming goes through: a view is its
   * name and its arrangement together, and two writes would be a window where
   * the tab says one thing and the rows show another.
   */
  const save = useCallback(
    async (view: SaveSegmentViewRequest, viewId?: string) => {
      const result = await client.saveSegmentView(view, viewId)
      if (result.status === 'saved') await load()
      return result
    },
    [client, load],
  )

  const remove = useCallback(
    async (viewId: string) => {
      await client.removeSegmentView(viewId)
      await load()
    },
    [client, load],
  )

  return { views, save, remove, reload: load }
}
